import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MenuItem, Select, Stack, Typography } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useTableSelector } from '../hooks/useTableSelector';
import type { TableSelectorProps } from '../types';

const SELECT_WIDTH = 490;

export const TableSelector = ({ options, selectedId, onChange }: TableSelectorProps) => {
  const { selectedIndex, onSelectChange } = useTableSelector({ options, selectedId, onChange });

  if (options.length === 0) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      sx={{ display: 'flex', alignItems: 'end' }}
    >
      <Select
        value={selectedId ?? ''}
        onChange={onSelectChange}
        size="small"
        inputProps={{ 'data-testid': 'analysis-tables-select' }}
        renderValue={(value) => {
          const selected = options.find((option) => option.id === value);
          return (
            <Typography variant="body1" noWrap>
              {selected?.label ?? ''}
            </Typography>
          );
        }}
        sx={{
          borderRadius: `${borderRadius.md}px`,
          display: 'flex',
          px: 2,
          alignItems: 'center',
          width: SELECT_WIDTH,
        }}
        MenuProps={{
          slotProps: {
            paper: { sx: { width: SELECT_WIDTH, borderRadius: `${borderRadius.md}px` } },
          },
        }}
      >
        {options.map((option) => {
          const isSelected = option.id === selectedId;
          return (
            <MenuItem
              key={option.id}
              value={option.id}
              data-testid={`analysis-tables-select-option-${option.id}`}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ width: '100%', minWidth: 0 }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {option.label}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" noWrap>
                    {option.countLabel}
                  </Typography>
                </Stack>
                {isSelected && (
                  <CheckCircleIcon
                    fontSize="small"
                    color="success"
                    data-testid={`analysis-tables-select-check-${option.id}`}
                  />
                )}
              </Stack>
            </MenuItem>
          );
        })}
      </Select>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="analysis-tables-select-position"
      >
        {selectedIndex + 1}/{options.length}
      </Typography>
    </Stack>
  );
};
